import {inject, injectable} from 'inversify';
import {makeAutoObservable, reaction, runInAction} from 'mobx';

import type {Nullable} from '../../utils/utility-types';

import {couponStore} from '../../api/coupon';
import {alert} from '../../components/alert';
import {autoassgnCoach} from '../../services/entity-action/client/autoassignCoach';
import {logger} from '../../utils/logger';
import {ASSETS_URL} from '../../utils/resources';
import {AsyncBusyManager, ReactionDisposeManager} from '../../utils/store';
import {FileModel} from '../domain/File/File.model';
import {CurrentUserService} from '../system/currentUser/CurrentUser.service';
import {EnvProviderService} from '../system/env/EnvProvider.service';
import {FetchUserWaitlistItemsUseCase} from '../useCases/User/FetchUserWaitlistItems.uc';

@injectable()
export class UserWaitlistStore {
  isLoaded = false;
  waitList = new Map<string, UserWaitlistStore.WItem>();
  private busy = new AsyncBusyManager<UserWaitlistStore.BusyKey>();
  private disposer = new ReactionDisposeManager();

  constructor(
    @inject(FetchUserWaitlistItemsUseCase)
    private readonly fetchUseCase: FetchUserWaitlistItemsUseCase,
    @inject(CurrentUserService)
    private readonly currentUserService: CurrentUserService,
    @inject(EnvProviderService)
    private readonly env: EnvProviderService,
  ) {
    this.busy.observe(this, {fetch: 'fetch'});

    makeAutoObservable(this);

    this.setupReactions();
  }

  get isFetching() {
    return this.busy.getReason('fetch');
  }

  get isWaitlisted() {
    return this.waitList.size > 0;
  }

  get list() {
    return Array.from(this.waitList.values()).map(wItem => wItem);
  }

  async fetch() {
    if (this.env.app.isB2b) {
      return;
    }

    try {
      const wItems = await this.fetchUseCase.execute();

      // just to make sure, but useCase should return active items only
      const activeWItems = wItems.filter(wi => !wi.joinedDate);

      runInAction(() => {
        this.waitList.clear();

        for (const wi of activeWItems) {
          const offer = wi.coupon?.defaultOffer;

          let programInfo = this.notFoundProgramInfo;

          if (offer) {
            programInfo = {
              avatarUrl:
                offer.avatar?.getSrc(FileModel.THUMBS.THUMB_500).url ?? null,
              conferenceDuration: offer.sessions.duration,
              conferencesCount: offer.sessions.count.total,
              description: offer.description || '',
              duration: offer.duration.value,
              title: offer.title,
            };
          }

          this.waitList.set(wi.id, {
            id: wi.id,
            isInvited: Boolean(wi.invitedDate),
            isJoining: false,
            position: wi.position,
            program: programInfo,
          });
        }
      });
    } finally {
      runInAction(() => {
        this.isLoaded = true;
      });
    }
  }

  async joinSpot(wId: string) {
    const user = this.currentUserService.user;

    const wItem = this.waitList.get(wId);

    if (!wItem) {
      return;
    }

    try {
      if (!wItem.isInvited || !user?.subtenant?.features.autoMatching) {
        throw new Error("You can't join at the moment");
      }

      runInAction(() => {
        wItem.isJoining = true;
      });

      const usedCoupon = await couponStore.apply({
        waitlist_item_id: wId,
      });

      if (!usedCoupon) {
        throw new Error("You can't join at the moment");
      }

      this.currentUserService.user!.metadata.last_used_coupon_id =
        usedCoupon._id;

      await this.currentUserService.user!.update({
        metadata: this.currentUserService.user!.metadata,
      });

      const automatchingResponse = await autoassgnCoach(usedCoupon!._id);

      this.fetch();

      return automatchingResponse;
    } catch (e) {
      logger.error(e);

      alert.error(e);

      throw e;
    } finally {
      runInAction(() => {
        wItem.isJoining = false;
      });
    }
  }

  private get notFoundProgramInfo(): UserWaitlistStore.WItem['program'] {
    return {
      avatarUrl: `${ASSETS_URL}/180x180.png`,
      conferenceDuration: 0,
      conferencesCount: 0,
      description: '',
      duration: 0,
      title: 'Program not found',
    };
  }

  private setupReactions() {
    this.disposer.watch(
      'currentUser',
      reaction(
        () => this.currentUserService.user,
        (next, prev) => {
          // when user is logged in
          if (prev === null && next !== null) {
            this.fetch();
          }
        },
        {fireImmediately: true},
      ),
    );
  }
}

export namespace UserWaitlistStore {
  export type BusyKey = 'fetch';

  export type WItem = {
    id: string;
    isInvited: boolean;
    isJoining: boolean;
    position: number;
    program: {
      avatarUrl: Nullable<string>;
      conferenceDuration: number;
      conferencesCount: number;
      description: string;
      duration: number;
      title: string;
    };
  };
}
